import { useMemo, useState, type ReactElement } from 'react'
import { PrefetchedTeamInfo } from 'app/components/entityInfo'
import {
  isDefined,
  isEmpty,
  isNotEmpty,
  isNotDefined,
  isTeamId,
  searchIncludes,
  type CompanyId,
  type TeamData,
  type TeamId,
} from '@terros/common'
import {
  CloseButton,
  Combobox,
  Input,
  InputBase,
  type InputBaseProps,
  Text,
  useCombobox,
  type ComboboxProps,
} from '@mantine/core'
import { useGroupedTeamOptions } from '@hooks'
import { useProfile, useTeams } from '@front/model'
import { DisplaySelectOption } from '../DisplayOption'
import { useTeamComboboxOptions } from './TeamComboboxOptions'

export type TeamInlineSelectExtraOption = {
  value: TeamId
  label: string
}

type TeamInlineSelectProps = Pick<InputBaseProps, 'size' | 'label' | 'description' | 'error' | 'required'> & {
  teamFilter?: (team: TeamData) => boolean
  companyId?: CompanyId
  width?: ComboboxProps['width']
  extraOptions?: TeamInlineSelectExtraOption[]
} & (
    | {
        value: TeamId | undefined
        onChange: (value: TeamId | undefined) => void
        allowDeselect: boolean
        placeholder: string
        emptyOptionLabel?: string
      }
    | {
        value: TeamId
        onChange: (value: TeamId) => void
        allowDeselect: false
      }
  )

export const TeamInlineSelect = ({
  teamFilter,
  companyId,
  width,
  size,
  label,
  description,
  error,
  required,
  extraOptions,
  ...props
}: TeamInlineSelectProps): ReactElement => {
  const [query, setQuery] = useState('')
  const { user, company } = useProfile()
  const { data: teams = [] } = useTeams({ companyId: companyId ?? user?.companyId })
  const availableTeams = useMemo(() => {
    if (!user) return []
    if (isEmpty(teams)) return []
    if (isDefined(teamFilter)) {
      return teams.filter(teamFilter)
    }
    return teams
  }, [teams, teamFilter, user])

  const filteredTeams = useMemo(() => {
    if (isEmpty(query)) return availableTeams
    return availableTeams.filter((team) =>
      searchIncludes([team.name, team.teamId, company?.levelNames?.[team.level]], query)
    )
  }, [availableTeams, query, company?.levelNames])

  const groupedTeamOptions = useGroupedTeamOptions(filteredTeams, company)

  const combobox = useCombobox({
    onDropdownOpen: () => {
      combobox.focusSearchInput()
      setQuery('')
    },
  })

  const currentTeam = useMemo(() => {
    return teams.find((team) => team.teamId === props.value)
  }, [teams, props.value])

  const selectedExtraOption = useMemo(() => {
    if (isNotDefined(props.value)) return undefined
    return extraOptions?.find((option) => option.value === props.value)
  }, [props.value, extraOptions])

  const placeholder = useMemo(() => {
    if (props.allowDeselect && isDefined(props.placeholder)) return props.placeholder
    return 'Select team'
  }, [props])

  const displayValue = useMemo(() => {
    if (selectedExtraOption) {
      return <Text size='sm'>{selectedExtraOption.label}</Text>
    }
    if (currentTeam) {
      return <PrefetchedTeamInfo linked={false} team={currentTeam} size='xs' textProps={{ size: 'sm' }} />
    }
    return <Input.Placeholder>{props.value || placeholder}</Input.Placeholder>
  }, [currentTeam, placeholder, props.value, selectedExtraOption])

  const options = useTeamComboboxOptions({
    groupedTeamOptions,
    availableTeams,
    selectedValue: isDefined(props.value) ? [props.value] : undefined,
  })

  const canClear = props.allowDeselect && isDefined(props.value)

  return (
    <Combobox
      store={combobox}
      width={width ?? 350}
      onOptionSubmit={(teamId) => {
        if (teamId === 'empty') {
          if (props.allowDeselect) {
            props.onChange(undefined)
          }
        } else if (isTeamId(teamId)) {
          if (props.allowDeselect) {
            const newValue = teamId === props.value ? undefined : teamId
            props.onChange(newValue)
          } else {
            if (!teamId || teamId === props.value) return
            props.onChange(teamId)
          }
        }
        combobox.closeDropdown()
      }}
      position='bottom-start'
    >
      <Combobox.Target>
        <InputBase
          size={size}
          label={label}
          description={description}
          error={error}
          required={required}
          pointer
          type='button'
          component='button'
          multiline
          styles={{
            input: {
              minHeight: 'auto',
              height: 'auto',
            },
          }}
          rightSection={
            canClear ? (
              <CloseButton
                size='sm'
                onClick={(e) => {
                  e.stopPropagation()
                  props.onChange(undefined)
                }}
                aria-label='Clear value'
              />
            ) : (
              <Combobox.Chevron />
            )
          }
          rightSectionPointerEvents={canClear ? 'all' : 'none'}
          onClick={() => combobox.toggleDropdown()}
        >
          {displayValue}
        </InputBase>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          placeholder='Search teams'
        />
        <Combobox.Options mah={400} style={{ overflowY: 'auto' }}>
          {isEmpty(query) && (
            <>
              {props.allowDeselect && isNotEmpty(props.emptyOptionLabel) && (
                <Combobox.Option value='empty'>
                  <DisplaySelectOption label={props.emptyOptionLabel} isSelected={isEmpty(props.value)} />
                </Combobox.Option>
              )}
              {isNotEmpty(extraOptions) &&
                extraOptions.map((option) => (
                  <Combobox.Option key={option.value} value={option.value}>
                    <DisplaySelectOption label={option.label} isSelected={props.value === option.value} />
                  </Combobox.Option>
                ))}
            </>
          )}
          {options}
          {isEmpty(options) && <Combobox.Empty>No teams found</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
